/*
 * MySiteCare
 *
 * Copyright © 2026 Jeanjen Sisoy Luberio
 * doing business as MySiteCare Website Application Services.
 * All rights reserved.
 *
 * DTI Business Name No. 7934250
 * https://mysitecare.eu
 *
 * This file contains original proprietary MySiteCare source code,
 * except for third-party components identified under their
 * respective licenses.
 */
import { useEffect, useState } from "react";
import SiteDashboardShell, { useSiteDashboard } from "@/components/customer/SiteDashboardShell";
import { fetchSiteCustomers, fetchSiteCustomer, patchSiteCustomerStatus, addSiteCustomerNote } from "@/lib/api";

const STATUSES = ["new_lead", "contacted", "customer", "repeat_customer", "inactive", "blocked"];

function CustomerDetail({ token, slug, customerId, onClose, onChanged }) {
  const [detail, setDetail] = useState(null);
  const [note, setNote] = useState("");

  const load = () => fetchSiteCustomer(token, slug, customerId).then(setDetail).catch(() => {});
  // `load` returns a Promise (its body is a .then()/.catch() chain) — passing it
  // directly as the effect callback makes React treat that Promise as an attempted
  // cleanup function and throw ("destroy is not a function"), crashing this modal.
  // Found live while auditing this exact anti-pattern elsewhere in the app.
  useEffect(() => { load(); }, [token, slug, customerId]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!detail) return null;
  return (
    <div className="fixed inset-0 z-40 flex items-start justify-center bg-black/30 p-4 overflow-y-auto" onClick={onClose}>
      <div className="mt-10 w-full max-w-lg rounded-2xl bg-white p-6 shadow-xl" onClick={(e) => e.stopPropagation()} data-testid="customer-detail-panel">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-bold font-display text-[#0F172A]">{detail.name}</h3>
          <button onClick={onClose} className="text-sm text-[#94A3B8] hover:text-[#0F172A]">Close</button>
        </div>
        <p className="mt-1 text-sm text-[#475569]">{detail.email || "—"} · {detail.phone || "—"}</p>
        <p className="mt-1 text-xs text-[#94A3B8]">Source: {detail.source}</p>

        <label htmlFor="customer-status" className="mt-4 block text-xs font-medium uppercase text-[#475569]">Status</label>
        <select id="customer-status" value={detail.status} onChange={(e) => patchSiteCustomerStatus(token, slug, customerId, e.target.value).then(() => { load(); onChanged(); })}
          className="mt-1 w-full rounded-lg border border-[#E2E8F0] px-3 py-2 text-sm bg-white">
          {STATUSES.map((s) => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
        </select>

        <p className="mt-4 text-xs font-medium uppercase text-[#475569]">Appointment history</p>
        {(detail.appointment_history || []).length === 0 ? (
          <p className="mt-1 text-sm text-[#94A3B8]">No appointments on file.</p>
        ) : (
          <ul className="mt-1 text-sm text-[#475569] space-y-1">
            {detail.appointment_history.map((a) => <li key={a.id}>{a.requested_date} — {a.status}</li>)}
          </ul>
        )}

        <p className="mt-4 text-xs font-medium uppercase text-[#475569]">Notes</p>
        <ul className="mt-1 space-y-2">
          {(detail.notes || []).map((n, i) => (
            <li key={i} className="text-sm text-[#475569] rounded-lg bg-[#F8FAFC] px-3 py-2">
              <p>{n.text}</p><p className="text-xs text-[#94A3B8] mt-0.5">{n.author} · {new Date(n.at).toLocaleDateString()}</p>
            </li>
          ))}
        </ul>
        <div className="mt-2 flex gap-2">
          <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add a private note…" className="flex-1 rounded-lg border border-[#E2E8F0] px-3 py-2 text-sm" />
          <button onClick={() => note && addSiteCustomerNote(token, slug, customerId, note).then(() => { setNote(""); load(); })} className="rounded-lg bg-[#0F172A] px-3 py-2 text-sm font-medium text-white">Add</button>
        </div>
      </div>
    </div>
  );
}

export default function SiteCustomers() {
  const dashboard = useSiteDashboard();
  const { token, slug } = dashboard;
  const [statusFilter, setStatusFilter] = useState("");
  const [search, setSearch] = useState("");
  const [customers, setCustomers] = useState(null);
  const [openId, setOpenId] = useState(null);

  const load = () => {
    if (!token) return;
    const params = {};
    if (statusFilter) params.status = statusFilter;
    if (search) params.search = search;
    fetchSiteCustomers(token, slug, params).then(setCustomers).catch(() => {});
  };
  useEffect(load, [token, slug, statusFilter, search]);

  return (
    <SiteDashboardShell active="customers" dashboard={dashboard}>
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <h2 className="text-lg font-bold font-display text-[#0F172A]">Customers &amp; Leads</h2>
        <div className="flex flex-wrap gap-2 w-full sm:w-auto">
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name or email…" className="min-w-0 flex-1 sm:flex-none rounded-lg border border-[#E2E8F0] px-3 py-1.5 text-sm" />
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="min-w-0 rounded-lg border border-[#E2E8F0] px-3 py-1.5 text-sm bg-white">
            <option value="">All statuses</option>
            {STATUSES.map((s) => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
          </select>
        </div>
      </div>

      <div className="rounded-2xl border border-[#E2E8F0] bg-white overflow-hidden">
        {!customers ? (
          <p className="p-6 text-sm text-[#475569]">Loading…</p>
        ) : customers.items.length === 0 ? (
          <p className="p-6 text-sm text-[#94A3B8]">No customers yet. New leads from your website will appear here.</p>
        ) : (
          customers.items.map((c) => (
            <button key={c.id} onClick={() => setOpenId(c.id)} className="flex w-full items-center justify-between gap-3 border-b border-[#E2E8F0] last:border-0 px-4 py-3.5 text-left hover:bg-[#F8FAFC]">
              <div><p className="text-sm font-medium text-[#0F172A]">{c.name}</p><p className="text-xs text-[#94A3B8]">{c.email || c.phone || "No contact on file"}</p></div>
              <span className="rounded-full bg-[#F1F5F9] px-2.5 py-1 text-xs font-medium capitalize text-[#475569]">{c.status.replace(/_/g, " ")}</span>
            </button>
          ))
        )}
      </div>

      {openId && <CustomerDetail token={token} slug={slug} customerId={openId} onClose={() => setOpenId(null)} onChanged={load} />}
    </SiteDashboardShell>
  );
}
